import React, { useCallback, useContext, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import SpacesIcon from '@atlaskit/icon-lab/core/spaces';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Box } from '@atlaskit/primitives/compiled/box';

import { Ati } from '@atlassian/ari';

import { AppNavigationContext } from '@confluence/app-navigation-context';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { useCreateSpaceStoreActions } from '@confluence/create-space/entry-points/createSpace.ts';
import { CREATE_SPACE_DIALOG } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import { fg } from '@confluence/feature-gating';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.space.title',
		defaultMessage: 'Space',
		description: 'title indicating that this option will create a space',
	},
});

export function useSpaceItem({ source, onClick }: UseMenuItemProps): UseMenuItemReturnProps {
	const { isRovoLLMEnabled } = useSessionData();
	const { formatMessage } = useIntl();
	const { openDrawer } = useCreateSpaceStoreActions();
	const { openModal } = useSmartCreationModalActions();
	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'create-space-button',
	});
	const { resetStickySearchRef, createContentPopoverButtonRef } = useContext(AppNavigationContext);
	const createSpaceButtonRef = useRef<HTMLElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			resetStickySearchRef?.current?.();
			const location = 'topCreateNav';

			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			if (isRovoLLMEnabled && fg('aifc_space_creation_enabled')) {
				openModal({ entryPoint: 'globalCreateSpaceButton', spaceKey: '' });
			} else {
				openDrawer(createContentPopoverButtonRef || createSpaceButtonRef, location);
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createSpaceButton',
					source,
					attributes: {
						location,
					},
				},
			}).fire();

			onClick('createSpacePopoverItem');
			e.stopPropagation();
		},
		[
			createAnalyticsEvent,
			createContentPopoverButtonRef,
			openDrawer,
			onClick,
			openModal,
			resetStickySearchRef,
			source,
			isRovoLLMEnabled,
		],
	);

	const preloadCreateSpace = () => {
		void preloadWebItemLocation({
			location: CREATE_SPACE_DIALOG,
		});
	};

	const PreloadSpaceWrap = ({ children }) => (
		<Box onMouseDown={preloadCreateSpace}>{children()}</Box>
	);

	return {
		ati: Ati.parse('ati:cloud:confluence:space'),
		children: formatMessage(messages.title),
		onClick: enforceStorageLimit(handleClick),
		elemBefore: SpacesIcon,
		testId: 'create-menu-button-item',
		Wrapper: PreloadSpaceWrap,
	};
}
